import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

const createHelpers = (width, height, css) => {
    // somehow sizes is ending up in markup, even if it is not a valid svg attribute
    // until we have a better solution, just render it empty, instead to '[Object object]'
    const sanitizeSizes = sizes =>
        Object.defineProperty(sizes, 'toString', {
            value: () => '',
            enumerable: false,
        });

    const getDimensions = (size, sizes) => {
        if (size && typeof size.width === 'number' && typeof size.height === 'number') {
            return size;
        }

        return size && sizes[size] ? sizes[size] : { width, height };
    };

    const getCss = (size, sizes, fillColor, fillColorRule, noStyles) => {
        if (noStyles) {
            return '';
        }

        const dimensions = getDimensions(size, sizes);
        const fillRule = fillColor && fillColorRule ? `${fillColorRule}{ fill: ${fillColor}; }` : '';

        return css`
            width: ${dimensions.width}px;
            height: ${dimensions.height}px;
            ${fillRule}
        `;
    };

    const propsToCss = ({ size, sizes, fillColor, fillColorRule, noStyles }) =>
        getCss(size, sizes, fillColor, fillColorRule, noStyles);

    return {
        getCss,
        getDimensions,
        propsToCss,
        sanitizeSizes,
    };
};

const width = '1024';
const height = '1024';
const viewBox = '0 0 1024 1024';

const { getDimensions, getCss, propsToCss, sanitizeSizes } = createHelpers(width, height, css);

const sizes = sanitizeSizes({
    small: { width: 16, height: 16 },
    medium: { width: 18, height: 18 },
    large: { width: 24, height: 24 },
});

const Image = styled.svg`
    ${propsToCss}
`;

const children = (
    <Fragment>
        <path
            d="M713.899 653.568h-31.616l-11.222-10.795a260.053 260.053 0 0059.264-213.717C711.51 317.824 618.581 228.949 506.496 215.339a260.395 260.395 0 00-291.115 290.986c13.611 112.043 102.486 204.886 213.803 223.702a260.395 260.395 0 00213.845-59.222l10.795 11.179V713.6l170.197 170.112a42.325 42.325 0 0059.648 0 42.24 42.24 0 000-59.648L713.9 653.568zm-240.256 0A179.883 179.883 0 01293.46 473.515 179.883 179.883 0 01473.6 293.376a179.883 179.883 0 01180.181 180.053A179.883 179.883 0 01473.6 653.525z"
            key="key-0"
        />
    </Fragment>
);

const defaultProps = {
    children,
    viewBox,
    fillColor: null,
    fillColorRule: '&&& path, &&& use, &&& g',
    sizes,
    size: null,
};

const propTypes = {
    fillColor: PropTypes.string,
    fillColorRule: PropTypes.string,
    viewBox: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    size: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            height: PropTypes.number.isRequired,
            width: PropTypes.number.isRequired,
        }),
    ]),
    sizes: PropTypes.shape({
        height: PropTypes.number,
        width: PropTypes.number,
    }),
};

export default Object.assign(Image, {
    getDimensions,
    getCss,
    defaultProps,
    propTypes,
    displayName: 'Search',
});
